/* IVIN delivery bar (snippets/ivin-delivery-bar.liquid). The cart drawer re-renders its footer with fresh HTML after
   every change, so this element re-reads the saved area each time it connects and animates from the last fill.
   Storage: every access goes through `store`, which touches window.localStorage / sessionStorage only inside a
   try — reading the property itself throws when site data is blocked (Safari private mode, blocked cookies). */
if (!customElements.get('ivin-delivery-bar')) {
  const KEY = 'ivin_zone';
  const FILL_KEY = 'ivin_zone_fill';

  // area = 'local' | 'session'; the Storage object is looked up inside the try (a default parameter or an argument
  // like `sessionStorage` would be evaluated outside it and throw before the try runs)
  const store = {
    get(k, area = 'local') { try { return window[`${area}Storage`].getItem(k); } catch (e) { return null; } },
    set(k, v, area = 'local') { try { window[`${area}Storage`].setItem(k, v); } catch (e) { /* private mode: the bar still works, it just won't remember */ } },
  };

  const pesos = (cents, lang) =>
    '$' + Math.ceil(cents / 100).toLocaleString(lang === 'en' ? 'en-US' : 'es-MX');

  // Gain framing only (Emilio, 2026-10-12): the cart never shows what delivery costs, only how close they are to free
  // delivery. The fee itself appears at checkout, once the address is in.
  const TEXT = {
    es: {
      intro: 'Elige tu zona y te decimos cuánto te falta para la entrega gratis.',
      pickup: 'Recoger en la tienda no tiene costo. Te avisamos en cuanto esté listo.',
      free: '<strong>Tu entrega va por nuestra cuenta.</strong>',
      left: (left) => `Te faltan <strong>${left}</strong> para tu entrega gratis.`,
      close: (left) => `¡Ya casi! Te faltan <strong>${left}</strong> para tu entrega gratis.`,
    },
    en: {
      intro: "Pick your area and we'll show how close you are to free delivery.",
      pickup: "Pick-up at the shop is free. We'll let you know as soon as it's ready.",
      free: "<strong>Delivery's on us.</strong>",
      left: (left) => `You're <strong>${left}</strong> away from free delivery.`,
      close: (left) => `Almost there! Just <strong>${left}</strong> to free delivery.`,
    },
  };

  // One request at a time, latest value wins: quick changes otherwise land out of order and the cart keeps an old area.
  let pending = null;
  let sending = false;
  let resent = null; // area already re-sent to a new cart on this page (drawer + cart page both connect)
  const queueSave = async (value) => {
    pending = value;
    if (sending) return;
    sending = true;
    while (pending !== null) {
      const v = pending;
      pending = null;
      try {
        const r = await fetch(`${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { 'Zona elegida': v } }),
        });
        if (!r.ok) console.warn('[ivin-delivery-bar] cart/update.js answered', r.status);
      } catch (e) {
        console.warn('[ivin-delivery-bar] could not save the area on the cart', e);
      }
    }
    sending = false;
  };

  class IvinDeliveryBar extends HTMLElement {
    connectedCallback() {
      this.lang = this.dataset.lang === 'en' ? 'en' : 'es';
      this.total = Number(this.dataset.total) || 0;
      try { this.zones = JSON.parse(this.dataset.zones); } catch (e) { this.zones = []; }
      this.select = this.querySelector('[data-ivin-zone]');
      this.msg = this.querySelector('[data-ivin-msg]');
      this.track = this.querySelector('[data-ivin-track]');
      this.fill = this.querySelector('[data-ivin-fill]');
      if (!this.select) return;

      const saved = store.get(KEY);
      if (saved && this.zones.some((z) => z.id === saved)) {
        this.select.value = saved;
        // a new cart (after checkout, or emptied and refilled) doesn't carry the area yet: send it once. The server
        // renders the cart's current value into data-cart-zone, so re-renders of the same cart don't post again.
        const z = this.zones.find((x) => x.id === saved);
        if (z && (this.dataset.cartZone || '') !== z.es && resent !== z.es) { resent = z.es; this.saveOnCart(); }
      }
      if (this._wired) { this.render(false); return; }
      this._wired = true;
      this.select.addEventListener('change', () => {
        store.set(KEY, this.select.value);
        document.dispatchEvent(new CustomEvent('ivin:zone', { detail: this.select.value })); // same-day line follows the area
        // keep every copy (drawer + cart page) in step
        document.querySelectorAll('ivin-delivery-bar').forEach((bar) => { if (bar !== this && bar.sync) bar.sync(this.select.value); });
        this.render(true);
        this.saveOnCart();
      });
      this.render(false);
    }

    sync(id) { if (this.select) { this.select.value = id; this.render(true); } }

    render(fromUser) {
      const t = TEXT[this.lang];
      const z = this.zones.find((x) => x.id === this.select.value);
      if (!z) {
        this.msg.textContent = t.intro;
        this.track.hidden = true;
        this.classList.remove('is-free'); // back to "choose your area" after a free one: no tick
        return;
      }
      let pct = 100, html;
      if (z.id === 'pickup') {
        html = t.pickup;
      } else if (this.total >= z.free) {
        html = t.free;
      } else {
        pct = Math.max(4, Math.min(100, (this.total / z.free) * 100));
        const left = pesos(z.free - this.total, this.lang);
        html = pct >= 75 ? t.close(left) : t.left(left);
      }
      this.msg.innerHTML = html;
      this.classList.toggle('is-free', pct >= 100);
      this.track.hidden = z.id === 'pickup';

      // animate from where the bar was before the drawer re-rendered, not from zero
      const last = Number(store.get(FILL_KEY, 'session'));
      if (!fromUser && Number.isFinite(last) && last > 0) {
        this.fill.style.transition = 'none';
        this.fill.style.transform = `scaleX(${last / 100})`;
        void this.fill.offsetWidth;
        this.fill.style.transition = '';
      }
      requestAnimationFrame(() => { this.fill.style.transform = `scaleX(${pct / 100})`; });
      store.set(FILL_KEY, String(pct), 'session');
    }

    // The chosen area travels with the order (shows in the admin and on the order screen); the address at checkout still decides the fee.
    saveOnCart() {
      const z = this.zones.find((x) => x.id === this.select.value);
      queueSave(z ? z.es : '');
    }
  }

  customElements.define('ivin-delivery-bar', IvinDeliveryBar);
}
